import type { JwtPayload } from "jwt-decode";
import type { Observable } from "rxjs";

import { jwtDecode } from "jwt-decode";
import pick from "lodash/pick";
import { toDataURL } from "qrcode";
import { BehaviorSubject } from "rxjs";
import { z } from "zod";

import type {
  AuthDataLoginFields,
  AuthDataStrategy,
  ConfigureMfaResult,
  CredentialsChangeSuccessResult,
  ForceChangePasswordResult,
  ForgotPasswordResult,
  LoginChallengeResult,
  LoginFailResult,
  LoginResult,
  LoginStatusEvent,
  LoginSuccessResult,
  LogoutEvent,
} from "../auth/types";
import type { AuthExchangeHandlerHelpers } from "../remote/authExchange";
import type {
  AuthenticationResult,
  AuthUserConfigureMfa,
  AuthUserConfirmForgotPassword,
  AuthUserCredentialsChangeSuccess,
  AuthUserForceChangePassword,
  AuthUserLoginChallenge,
  AuthUserLoginToken,
  ConfigureMfaInput,
  ConfirmForgotPasswordInput,
  ForceChangePasswordInput,
  ForgotPasswordInput,
  LoginChallengeParameters,
  LoginParameters,
} from "../remote/restTypes";
import type { LoggerType } from "./log";

import { AuthData } from "../auth/authData";
import { ClientError } from "./error";
import { getLogger } from "./log";

export interface IAuthHandler extends AuthExchangeHandlerHelpers {
  login: (username: string, password: string) => Promise<LoginResult>;
  getUserId: () => string;
  getUsername: () => string;
  isLoggedIn: () => boolean;
  loginStatus: Observable<LoginStatusEvent>;
  configureMfa: (
    configureMfa: ConfigureMfaResult,
    code: string,
  ) => Promise<LoginResult>;
  forceChangePassword: (
    forceChangePassword: ForceChangePasswordResult,
    newPassword: string,
  ) => Promise<LoginResult>;
  loginChallenge: (
    challengeResponse: LoginChallengeResult,
    code: string,
  ) => Promise<LoginResult>;
  // TODO fetchAuthenticatedPost may be removed in future
  //  or moved to another interface
  fetchAuthenticatedPost: (
    resourcePath: string,
    bodyObject: Record<string, any>,
  ) => Promise<Response>;
  forgotPassword: (username: string) => Promise<LoginResult>;
  confirmForgotPassword: (
    userName: string,
    newPassword: string,
    code: string,
  ) => Promise<LoginResult>;
  logoutWithServerInvalidation: (event?: LogoutEvent) => Promise<void>;
}

const LOGIN_INITIAL_ENDPOINT = "/login";
const LOGIN_CHALLENGE_ENDPOINT = "/loginChallenge";
const CONFIGURE_MFA_ENDPOINT = "/configureMfa";
const CHANGE_PASSWORD_ENDPOINT = "/forceChangePassword";
const FORGOT_PASSWORD_ENDPOINT = "/forgotPassword";
const CONFIRM_FORGOT_PASSWORD_ENDPOINT = "/confirmForgotPassword";
const REFRESH_TOKEN_ENDPOINT = "/refreshToken";
const LOGOUT_ENDPOINT = "/auth/logout";

interface ErrorResponseJson {
  error: string;
  [key: string]: any;
}

type AuthenticationResultOrError =
  | AuthenticationResult
  | ErrorResponseJson
  | string
  | null;

interface AuthHandlerArgs {
  url: string;
  authData?: AuthDataStrategy;
  onLoginStatusChange?: (loginStatusEvent: LoginStatusEvent) => unknown;
}

const refreshTokenResultSchema = z.object({
  accessToken: z.string().min(1),
  expiresIn: z.number().nonnegative(),
});

const cognitoRefreshTokenResultSchema = z.object({
  access_token: z.string().min(1),
  expires_in: z.number().nonnegative(),
});

export class AuthHandler implements IAuthHandler {
  private logger: LoggerType;
  private url: string;
  private authData: AuthDataStrategy;
  private loginStatusSubject = new BehaviorSubject<LoginStatusEvent>({
    loggedIn: false,
  });
  private onLoginStatusChangeCallback: (
    loginStatusEvent: LoginStatusEvent,
  ) => unknown;

  constructor(args: AuthHandlerArgs) {
    this.logger = getLogger("AuthHandler");
    this.url = args.url;
    this.authData =
      args.authData ?? new AuthData({ url: this.url, allowLocalStorage: true });
    this.onLoginStatusChangeCallback =
      args.onLoginStatusChange ??
      (() => {
        /* noop */
      });
    if (this.isLoggedIn()) {
      this.emitLoginStatusEvent({
        loggedIn: true,
        data: this.createSuccessResponse(this.getLoginStatusFields()),
      });
    }
    if (this.authData.dataChangeObservable) {
      this.authData.dataChangeObservable.subscribe(
        this.onDataStrategyDetectedChange,
      );
    }
  }

  fetchAuthenticatedPost = async (
    resourcePath: string,
    bodyObject: Record<string, any>,
  ): Promise<Response> => {
    if (!this.isLoggedIn()) {
      throw this.notAuthErr("fetchAuthenticatedPost");
    }
    if (!resourcePath.startsWith("/")) {
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      return Promise.reject("resourcePath must start with /");
    }
    if (resourcePath.includes("://")) {
      // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
      return Promise.reject("resourcePath cannot contain protocol");
    }
    return fetch(this.url + resourcePath, {
      headers: {
        "Content-Type": "application/json",
        authorization: `Bearer ${this.getToken()?.token}`,
      },
      method: "POST",
      body: JSON.stringify(bodyObject),
    });
  };

  loginStatus: Observable<LoginStatusEvent> =
    this.loginStatusSubject.asObservable();

  login: IAuthHandler["login"] = async (username, password) => {
    //
    // initiate login:  (user, pwd) => token response || mfa challenge || error
    const res = await fetch(
      this.url + LOGIN_INITIAL_ENDPOINT,
      fetchInit<LoginParameters>({
        authParams: {
          username,
          password,
        },
      }),
    );

    return this.parseAndReturnLoginResult({ res, caller: "login", username });
  };

  loginChallenge: IAuthHandler["loginChallenge"] = async (
    challengeResponse,
    code,
  ) => {
    if (!isLoginChallenge(challengeResponse)) {
      const msg = "Invalid challenge response";
      this.logger.error(msg, challengeResponse);
      throw new ClientError(msg, challengeResponse);
    }

    const {
      session,
      challengeKind,
      authParams: { username },
    } = challengeResponse;

    if (!session || !challengeKind || !username) {
      const msg = "Challenge response is missing required fields";
      this.logger.error(msg, challengeResponse);
      throw new ClientError(msg, challengeResponse);
    }

    const res = await fetch(
      this.url + LOGIN_CHALLENGE_ENDPOINT,
      fetchInit<LoginChallengeParameters>({
        session,
        challengeKind,
        authParams: {
          username,
          softwareTokenMfaCode: code,
        },
      }),
    );

    return this.parseAndReturnLoginResult({
      res,
      caller: "loginChallenge",
      username,
    });
  };

  forceChangePassword: IAuthHandler["forceChangePassword"] = async (
    forceChangePasswordResult,
    newPassword,
  ) => {
    if (!isForceChangePassword(forceChangePasswordResult)) {
      const msg = "Invalid challenge response";
      this.logger.error(msg, forceChangePasswordResult);
      throw new ClientError(msg, forceChangePasswordResult);
    }

    const {
      session,
      authParams: { username },
    } = forceChangePasswordResult;

    if (!session || !username) {
      const msg = "Force change password response is missing required fields";
      this.logger.error(msg, forceChangePasswordResult);
      throw new ClientError(msg, forceChangePasswordResult);
    }

    const res = await fetch(
      this.url + CHANGE_PASSWORD_ENDPOINT,
      fetchInit<ForceChangePasswordInput>({
        session,
        authParams: {
          username,
        },
        newPassword,
      }),
    );

    return this.parseAndReturnLoginResult({
      res,
      caller: "configureMfa",
      username,
    });
  };

  configureMfa: IAuthHandler["configureMfa"] = async (
    configureMfaResult,
    code,
  ) => {
    if (!isConfigureMfa(configureMfaResult)) {
      const msg = "Invalid challenge response";
      this.logger.error(msg, configureMfaResult);
      throw new ClientError(msg, configureMfaResult);
    }

    const {
      session,
      authParams: { username },
    } = configureMfaResult;

    if (!session || !username) {
      const msg = "Challenge response is missing required fields";
      this.logger.error(msg, configureMfaResult);
      throw new ClientError(msg, configureMfaResult);
    }

    const res = await fetch(
      this.url + CONFIGURE_MFA_ENDPOINT,
      fetchInit<ConfigureMfaInput>({
        session,
        totpSecret: configureMfaResult.totpSecret,
        authParams: {
          username,
          softwareTokenMfaCode: code,
        },
      }),
    );

    return this.parseAndReturnLoginResult({
      res,
      caller: "configureMfa",
      username,
    });
  };

  forgotPassword: IAuthHandler["forgotPassword"] = async (username) => {
    //
    // forgotPassword:  (user) => ForgotPasswordResponse || error
    const res = await fetch(
      this.url + FORGOT_PASSWORD_ENDPOINT,
      fetchInit<ForgotPasswordInput>({
        username,
      }),
    );

    const body:
      | AuthUserConfirmForgotPassword
      | AuthUserCredentialsChangeSuccess = await this.getJson(res);

    if (body.__typename === "AuthUserConfirmForgotPassword") {
      // if we do use cognito to reset, we need the user to enter a code sent
      // by cognito -- this is not currently in use due to some cognito restrictions
      const result: ForgotPasswordResult = {
        type: "ForgotPasswordResult",
        username: body.username,
        codeDeliveryDetails: body.$metadata.codeDeliveryDetails,
        predicates,
      };
      return result;
    } else if (body.__typename === "AuthUserCredentialsChangeSuccess") {
      // if we use a reset to temp password, they don't need to enter a code,
      // the server will just email them a new temp password; The response includes
      // a generic message, without information if provided email was valid.
      // This prevents user enumeration.
      const result: CredentialsChangeSuccessResult = {
        type: "CredentialsChangeSuccessResult",
        message: body.message,
        predicates,
      };
      return result;
    } else {
      throw new Error("Unexpected response type for operation forgotPassword");
    }
  };

  confirmForgotPassword = async (
    username: string,
    newPassword: string,
    code: string,
  ): Promise<LoginResult> => {
    const res = await fetch(
      this.url + CONFIRM_FORGOT_PASSWORD_ENDPOINT,
      fetchInit<ConfirmForgotPasswordInput>({
        newPassword,
        code,
        username,
      }),
    );

    return this.parseAndReturnLoginResult({
      res,
      caller: "confirmForgotPassword",
      username,
    });
  };

  isLoggedIn = () => !!this.authData.userId && !!this.authData.token;

  getUserId = () => {
    if (!this.isLoggedIn() || !this.authData.userId) {
      throw this.notAuthErr("getUserId");
    }
    return this.authData.userId;
  };

  getUsername = () => {
    if (!this.isLoggedIn() || !this.authData.username) {
      throw this.notAuthErr("getUsername");
    }
    return this.authData.username;
  };

  getToken = () => {
    if (!this.isLoggedIn() || !this.authData.token) {
      return undefined;
    }
    return { token: this.authData.token, expires: this.authData.expires };
  };

  getRefreshToken = () => {
    if (!this.isLoggedIn() || !this.authData.refreshToken) {
      return undefined;
    }
    return this.authData.refreshToken;
  };

  refreshToken = async () => {
    if (!this.isLoggedIn() || !this.authData.refreshToken) {
      throw this.notAuthErr("refreshToken");
    }
    const refreshProvider = this.authData.refreshProvider ?? { type: "api" };
    const isCognito = refreshProvider.type === "cognito";
    const res = isCognito
      ? await fetch(refreshProvider.tokenUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams({
            grant_type: "refresh_token",
            client_id: refreshProvider.clientId,
            refresh_token: this.authData.refreshToken,
          }),
        })
      : await fetch(
          this.url + REFRESH_TOKEN_ENDPOINT,
          fetchInit<{ refreshToken: string }>({
            refreshToken: this.authData.refreshToken,
          }),
        );

    return this.parseAndReturnRefreshTokenResult(res, isCognito);
  };

  logout = (event?: LogoutEvent) => {
    this.authData.clearLoginFields();
    this.emitLoginStatusEvent({
      loggedIn: false,
      data: event ?? undefined,
    });
  };

  logoutWithServerInvalidation = async (event?: LogoutEvent): Promise<void> => {
    if (this.isLoggedIn()) {
      const accessToken = this.authData.token;
      const refreshToken = this.authData.refreshToken;
      try {
        const response = await fetch(this.url + LOGOUT_ENDPOINT, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ refreshToken }),
        });
        if (!response.ok) {
          this.logger.warn(
            `Server-side logout returned non-2xx: ${response.status}; continuing with local logout`,
          );
        }
      } catch (err) {
        this.logger.warn(
          "Server-side logout request failed; continuing with local logout",
          err,
        );
      }
    }
    this.logout(event);
  };

  private emitLoginStatusEvent = (loginStatusEvent: LoginStatusEvent) => {
    this.loginStatusSubject.next(loginStatusEvent);
    this.onLoginStatusChangeCallback(loginStatusEvent);
  };

  protected getTokenResponse = () => this.authData.raw;

  private parseAndReturnLoginResult = async ({
    res,
    caller,
    username,
  }: {
    res: Response;
    caller: string;
    username: string;
  }) => {
    const body: AuthenticationResult = await this.getJson(res);
    this.logger.trace(`${caller}: init login response`, { body, res });

    if ((res.status >= 400 && res.status <= 403) || res.status === 429) {
      return this.createLoginFailResult(res.status, body);
    } else if (res.status < 200 || res.status >= 300) {
      return getErrorRejection(res, body);
    }

    if (isConfigureMfaResponse(body)) {
      return Object.freeze(await this.createConfigureMfaResponse(body));
    }

    if (isForceChangePasswordResponse(body)) {
      return Object.freeze(this.createForceChangePasswordResponse(body));
    }

    if (isChallengeResponse(body)) {
      // freeze the challenge response to prevent accidental mod of 'next' data
      return Object.freeze(this.createChallengeResponse(body));
    }

    if (isCredentialsChangeSuccessResponse(body)) {
      return Object.freeze(this.createCredentialsChangeSuccessResponse(body));
    }

    const loginFields = this.extractLoginFields(body, username);

    this.authData.setLoginFields(loginFields);

    const successResult = this.createSuccessResponse(loginFields);
    this.emitLoginStatusEvent({ loggedIn: true, data: successResult });
    return successResult;
  };

  private parseAndReturnRefreshTokenResult = async (
    res: Response,
    isCognito = false,
  ) => {
    const body = await this.getJson(res);
    let data: z.infer<typeof refreshTokenResultSchema>;
    if (isCognito) {
      const result = cognitoRefreshTokenResultSchema.safeParse(body);
      if (!result.data) return false;
      data = {
        accessToken: result.data.access_token,
        expiresIn: result.data.expires_in,
      };
    } else {
      const result = refreshTokenResultSchema.safeParse(body);
      if (!result.data) return false;
      data = result.data;
    }

    const { expires, userId, userRole } = this.parseAccessToken(
      data.accessToken,
      data.expiresIn,
    );

    this.authData.updateTokenFields({
      expires,
      raw: data,
      token: data.accessToken,
      userId,
      userRole,
    });

    return true;
  };

  private createSuccessResponse = (
    loginFields: Pick<AuthDataLoginFields, "userId" | "userRole" | "expires">,
  ): LoginSuccessResult => {
    const loginSuccessResult = {
      ...pick(loginFields, ["userId", "userRole", "expires"]),
      type: "LoginSuccessResult",
      predicates,
    };

    if (!isLoginSuccess(loginSuccessResult)) {
      throw new Error("Bad login response: required fields not found in token");
    }

    return loginSuccessResult;
  };

  onDataStrategyDetectedChange = () => {
    const statusResult: LoginSuccessResult | LogoutEvent = this.isLoggedIn()
      ? this.createSuccessResponse(this.getLoginStatusFields())
      : { type: "SessionExpiredEvent", trigger: "expiry", payload: {} };
    this.emitLoginStatusEvent({
      loggedIn: this.isLoggedIn(),
      data: statusResult,
    });
  };

  private getTotpQrLink = (username: string, totpSecret: string) => {
    const thisUrl = "integration.fake.local";
    // todo consider moving otpauth url generation to server
    return `otpauth://totp/${thisUrl}:${encodeURI(username)}?secret=${encodeURI(
      totpSecret.replaceAll(" ", ""),
    )}&issuer=alaffia`;
  };

  private createConfigureMfaResponse = async (
    challengePayload: AuthUserConfigureMfa,
  ): Promise<ConfigureMfaResult> => {
    let totpQrCode = challengePayload?.totpQrCode;

    if (!totpQrCode && !!challengePayload?.totpSecret) {
      try {
        totpQrCode = await toDataURL(
          this.getTotpQrLink(
            challengePayload.authParams.username,
            challengePayload.totpSecret,
          ),
          {
            errorCorrectionLevel: "H",
          },
        );
      } catch (e: any) {
        this.logger.error("Failed to generate QR code", { error: e });
      }
    }

    return {
      type: "ConfigureMfaResult",
      session: challengePayload.session,
      totpSecret: challengePayload.totpSecret,
      totpQrCode, // either sent by IDP or built above
      authParams: challengePayload.authParams,
      predicates,
    };
  };

  private createForceChangePasswordResponse = (
    challengePayload: AuthUserForceChangePassword,
  ): ForceChangePasswordResult => {
    return {
      type: "ForceChangePasswordResult",
      session: challengePayload.session,
      idpType: challengePayload.idpType,
      authParams: challengePayload.authParams,
      predicates,
    };
  };

  private createChallengeResponse = (
    challengePayload: AuthUserLoginChallenge,
  ): LoginChallengeResult => {
    return {
      type: "LoginChallengeResult",
      challengeKind: challengePayload.challengeKind,
      session: challengePayload.session,
      authParams: challengePayload.authParams,
      predicates,
    };
  };

  private createCredentialsChangeSuccessResponse = (
    credentialsChangeSuccessResponse: AuthUserCredentialsChangeSuccess,
  ): CredentialsChangeSuccessResult => {
    return {
      type: "CredentialsChangeSuccessResult",
      message: credentialsChangeSuccessResponse.message,
      predicates,
    };
  };

  private createLoginFailResult = (
    httpStatus: number,
    responseBody: any,
  ): LoginFailResult => {
    return {
      type: "LoginFailResult",
      responseBody,
      httpStatus,
      predicates,
    };
  };

  private parseAccessToken = (accessToken: string, expiresIn: number) => {
    const jwt: JwtPayload & Record<string, any> = jwtDecode(accessToken);

    // todo: we could vet this more and potentially adjust (sync) expires to clients
    //  clock if based on iat... ~e.g. jwt.iat ? Date.now() - jwt.iat * 1000 : <???>,
    const expires = jwt.exp ? jwt.exp * 1000 : Date.now() + expiresIn * 1000;

    const userId = jwt["cognito:groups"]?.[1] ?? jwt.sub;
    const userRole = jwt["cognito:groups"]?.[0] ?? null;

    if (!userId) {
      const msg = "Unexpected error, no userId in token response";
      this.logger.error(msg);
      throw new ClientError(msg);
    }

    return {
      expires,
      userId,
      userRole,
    };
  };

  private extractLoginFields = (
    tokenResponse: AuthenticationResultOrError,
    username: string,
  ): AuthDataLoginFields => {
    if (!isTokenResponse(tokenResponse)) {
      const msg = "Unexpected error, no token response";
      this.logger.error(msg, tokenResponse);
      throw new ClientError(msg, tokenResponse);
    }

    const { accessToken } = tokenResponse;
    if (!accessToken) {
      const msg = "Unexpected error, no access token returned";
      this.logger.error(msg, tokenResponse);
      throw new ClientError(msg, tokenResponse);
    }

    const { expires, userId, userRole } = this.parseAccessToken(
      accessToken,
      tokenResponse.expiresIn ?? 0,
    );

    return {
      url: this.url,
      userId,
      username,
      userRole,
      token: tokenResponse.accessToken ?? null,
      expires,
      refreshToken: tokenResponse.refreshToken ?? null,
      refreshProvider: { type: "api" },
      raw: tokenResponse,
    };
  };

  private getJson = async (res: Response) =>
    Promise.resolve()
      .then(() => res.json())
      .catch((error) => {
        this.logger.error("Failed to retrieve response body", { error, res });
        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors
        return Promise.reject(error);
      });

  private notAuthErr = (contextMessage: string) => {
    const msg = `${contextMessage}: must login first!`;
    this.logger.error(msg);
    return new ClientError(msg);
  };

  private getLoginStatusFields = () => {
    return {
      userId: this.authData.userId,
      userRole: this.authData.userRole,
      expires: this.authData.expires,
    };
  };
}

// exported convenience functions

export function isLoginSuccess(
  loginResult: unknown,
): loginResult is LoginSuccessResult {
  return (
    hasType(loginResult) &&
    loginResult.type === "LoginSuccessResult" &&
    hasProperty(loginResult, "userId") &&
    !!loginResult.userId &&
    hasProperty(loginResult, "expires") &&
    !!loginResult.expires
  );
}

export function isForceChangePassword(
  loginResult: unknown,
): loginResult is ForceChangePasswordResult {
  return (
    hasType(loginResult) && loginResult.type === "ForceChangePasswordResult"
  );
}

export function isForgotPassword(
  loginResult: unknown,
): loginResult is ForgotPasswordResult {
  return hasType(loginResult) && loginResult.type === "ForgotPasswordResult";
}

export function isConfigureMfa(
  loginResult: unknown,
): loginResult is ConfigureMfaResult {
  return (
    hasType(loginResult) &&
    loginResult.type === "ConfigureMfaResult" &&
    hasProperty(loginResult, "totpSecret") &&
    !!loginResult.totpSecret
  );
}

export function isLoginChallenge(
  loginResult: unknown,
): loginResult is LoginChallengeResult {
  return (
    hasType(loginResult) &&
    loginResult.type === "LoginChallengeResult" &&
    hasProperty(loginResult, "session") &&
    !!loginResult.session &&
    hasProperty(loginResult, "challengeKind") &&
    !!loginResult.challengeKind &&
    hasProperty(loginResult, "authParams") &&
    !!loginResult.authParams
  );
}

export function isLoginFail(
  loginResult: unknown,
): loginResult is LoginFailResult {
  return (
    hasType(loginResult) &&
    loginResult.type === "LoginFailResult" &&
    hasProperty(loginResult, "httpStatus") &&
    typeof loginResult.httpStatus === "number" &&
    loginResult.httpStatus >= 400 &&
    loginResult.httpStatus < 500
  );
}

export function isCredentialsChangeSuccess(
  loginResult: unknown,
): loginResult is CredentialsChangeSuccessResult {
  return (
    hasType(loginResult) &&
    loginResult.type === "CredentialsChangeSuccessResult"
  );
}

function hasType(value: unknown) {
  return hasProperty(value, "type");
}

const predicates = {
  isLoginSuccess,
  isConfigureMfa,
  isForceChangePassword,
  isForgotPassword,
  isLoginChallenge,
  isLoginFail,
  isCredentialsChangeSuccess,
};

// helpers

function isErrorResponseJsonBody(
  authResult: string | AuthenticationResultOrError,
): authResult is ErrorResponseJson {
  return hasProperty(authResult, "error");
}

function isConfigureMfaResponse(
  authResult: AuthenticationResultOrError,
): authResult is AuthUserConfigureMfa {
  return (
    hasTypename(authResult) &&
    authResult?.__typename === "AuthUserConfigureMfa" && // TODO  AuthUserConfigureMfa ??
    !!authResult?.session &&
    !!authResult?.totpSecret
  );
}

function isChallengeResponse(
  authResult: AuthenticationResultOrError,
): authResult is AuthUserLoginChallenge {
  return (
    hasTypename(authResult) &&
    authResult?.__typename === "AuthUserLoginChallenge" &&
    !!authResult?.session &&
    !!authResult?.challengeKind
  );
}

function isForceChangePasswordResponse(
  authResult: AuthenticationResultOrError,
): authResult is AuthUserForceChangePassword {
  return (
    hasTypename(authResult) &&
    authResult.__typename === "AuthUserForceChangePassword"
  );
}

function isTokenResponse(
  authResult: AuthenticationResultOrError,
): authResult is AuthUserLoginToken {
  return (
    hasTypename(authResult) && authResult.__typename === "AuthUserLoginToken"
  );
}

function isCredentialsChangeSuccessResponse(
  authResult: AuthenticationResultOrError,
): authResult is AuthUserCredentialsChangeSuccess {
  return (
    hasTypename(authResult) &&
    authResult.__typename === "AuthUserCredentialsChangeSuccess"
  );
}

function hasTypename(value: unknown) {
  return hasProperty(value, "__typename");
}

function hasProperty<TProp extends string>(
  value: unknown,
  prop: TProp,
): value is Record<TProp, unknown> {
  return Boolean(value && typeof value === "object" && prop in value);
}

function getErrorRejection(
  res: Response,
  body: string | AuthenticationResultOrError,
) {
  const msg = isErrorResponseJsonBody(body)
    ? `Login Failed: [${res.status}]: ${body.error}`
    : `Login Failed: [${res.status}]: ${String(body)}`;
  return Promise.reject(
    new ClientError(msg, {
      response: res,
      body: body,
    }),
  );
}

function fetchInit<T>(bodyObj: T) {
  return {
    method: "POST",
    body: JSON.stringify(bodyObj),
    headers: {
      "Content-Type": "application/json",
    },
  };
}
